import type { ReactNode } from 'react';

import styles from './ChoiceCards.module.css';

export interface Choice<T extends string> {
  value: T;
  title: string;
  description: string;
  /** A glyph for the card's badge. */
  icon: ReactNode;
  /** Shown instead of choosing, e.g. "Coming soon"; the card is disabled. */
  unavailable?: string;
}

export interface ChoiceCardsProps<T extends string> {
  label: string;
  choices: Choice<T>[];
  onChoose: (value: T) => void;
}

/** Big, tappable cards for a one-of choice that decides what comes next. */
export function ChoiceCards<T extends string>({ label, choices, onChoose }: ChoiceCardsProps<T>) {
  return (
    <ul className={styles.grid} aria-label={label}>
      {choices.map((choice) => (
        <li key={choice.value}>
          <button
            type="button"
            className={styles.card}
            disabled={Boolean(choice.unavailable)}
            onClick={() => onChoose(choice.value)}
          >
            <span className={styles.icon} aria-hidden="true">
              {choice.icon}
            </span>
            <span className={styles.text}>
              <span className={styles.title}>
                {choice.title}
                {choice.unavailable ? (
                  <span className={styles.chip}>{choice.unavailable}</span>
                ) : null}
              </span>
              <span className={styles.description}>{choice.description}</span>
            </span>
            {choice.unavailable ? null : (
              <span className={styles.chevron} aria-hidden="true">
                ›
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
